import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, Loader2, Smartphone, History, Star, Bug, Copy, LayoutDashboard } from "lucide-react";
import { db, auth } from '../firebase/config';
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  badge: string;
  billingInterval: 'weekly' | 'monthly' | 'yearly' | 'one-time';
  prices: {
    KES: number;
    USD?: number;
  };
  freeTrialDays: number;
  setupFee: number;
  features: string[];
  visibility: 'public' | 'hidden' | 'archived';
  order: number;
}

const SubscriptionManagement = () => {
  const [loading, setLoading] = useState(false);
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'active' | 'expired' | 'trial' | 'none'>('none');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [defaultTrialDays, setDefaultTrialDays] = useState(14);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  
  const navigate = useNavigate();
  const reduxCurrentUser = useSelector((state: any) => state.users?.currentUser);
  const prevReduxStatus = useRef<string | undefined>(undefined);

  // Sync local state with Redux for real-time updates and handle redirection
  useEffect(() => {
    if (reduxCurrentUser?.subscriptionStatus) {
      // merry christmass
      const newStatus = reduxCurrentUser.subscriptionStatus;
      setSubscriptionStatus(newStatus);
      
      if (reduxCurrentUser.subscriptionExpiry) {
        setExpiryDate(new Date(reduxCurrentUser.subscriptionExpiry));
      }

      // Auto-redirect ONLY if status transitions to active from something else (e.g. just paid)
      if (newStatus === 'active' && prevReduxStatus.current && prevReduxStatus.current !== 'active' && prevReduxStatus.current !== 'trial') {
         console.log("SubscriptionManagement - Success transition detected (was expired/none):", prevReduxStatus.current, "->", newStatus);
         setShowSuccessDialog(true);
      } else if (newStatus === 'active' && prevReduxStatus.current && prevReduxStatus.current !== 'active') {
          console.log("SubscriptionManagement - Activation detected (trial or null):", prevReduxStatus.current, "->", newStatus);
          toast({
            title: "Subscription Activated!",
            description: "Refreshing session...",
            variant: "default",
          });
          setTimeout(() => {
            window.location.assign('/dashboard');
          }, 1500);
      }
      
      prevReduxStatus.current = newStatus;
    }
  }, [reduxCurrentUser?.subscriptionStatus, reduxCurrentUser?.subscriptionExpiry]);

  // Debug state - STK Push focused
  const [showDebug, setShowDebug] = useState(false);
  const [lastPaymentError, setLastPaymentError] = useState<string | null>(null);
  const [lastPaymentData, setLastPaymentData] = useState<any>(null);
  const [userClaims, setUserClaims] = useState<any>(null);
  const [debugLogs, setDebugLogs] = useState<{type: 'info' | 'error' | 'success', message: string, data?: any, timestamp: number}[]>([]);

  // Helper to add debug log entries
  const addDebugLog = (type: 'info' | 'error' | 'success', message: string, data?: any) => {
    setDebugLogs(prev => [...prev, { type, message, data, timestamp: Date.now() }]);
    console.log(`[DEBUG ${type.toUpperCase()}]`, message, data);
  };

  const copyDebugInfo = () => {
    const logsFormatted = debugLogs.map(log => 
      `[${log.type.toUpperCase()}] ${new Date(log.timestamp).toLocaleTimeString()} - ${log.message}${log.data ? '\n' + JSON.stringify(log.data, null, 2) : ''}`
    ).join('\n\n');

    const debugInfo = `
=== STK PUSH DEBUG INFO ===
Generated: ${new Date().toISOString()}

--- PAYMENT DATA SENT TO FUNCTION ---
phoneNumber: ${lastPaymentData?.phoneNumber || 'NOT SET'} ${!lastPaymentData?.phoneNumber ? '❌ MISSING' : '✓'}
amount: ${lastPaymentData?.amount || 'NOT SET'} ${!lastPaymentData?.amount ? '❌ MISSING' : '✓'}
schoolId: ${lastPaymentData?.schoolId || 'NOT SET'} ${!lastPaymentData?.schoolId ? '❌ MISSING' : '✓'}
planId: ${lastPaymentData?.planId || 'NOT SET'} ${!lastPaymentData?.planId ? '❌ MISSING' : '✓'}

--- USER AUTH CLAIMS ---
schoolID: ${userClaims?.schoolID || 'NOT SET'}
role: ${userClaims?.defaultAffiliation?.role || 'NOT SET'}
uid: ${userClaims?.user_id || 'NOT SET'}

--- CURRENT STATE ---
School ID: ${schoolId || 'NOT SET'}
Subscription Status: ${subscriptionStatus}
Plans Loaded: ${plans.length}

--- SELECTED PLAN ---
${selectedPlan ? JSON.stringify(selectedPlan, null, 2) : 'No plan selected'}

--- LAST ERROR ---
${lastPaymentError || 'None'}

--- EVENT LOGS (${debugLogs.length}) ---
${logsFormatted || 'No logs yet'}
    `.trim();
    
    navigator.clipboard.writeText(debugInfo);
    toast({ title: "Copied!", description: "Debug info copied to clipboard." });
  };

  const fetchSubscriptionDetails = async (sid: string) => {
    try {
      // Use real-time listener for the subscription page
      const schoolRef = doc(db, `schools/${sid}/schoolDetails/main`);
      
      const unsubscribe = onSnapshot(schoolRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.subscriptionStatus) {
            setSubscriptionStatus((prev: string | undefined) => {
              const newStatus = data.subscriptionStatus;
              
              const isTransitioning = newStatus === 'active' && (prev === 'expired' || prev === 'none' || prev === 'inactive');
              
              if (isTransitioning) {
                 console.log("SubscriptionManagement - LOCAL snapshot transition matched:", prev, "->", newStatus);
                 setShowSuccessDialog(true);
              }
              return newStatus;
            });
            
            if (data.subscriptionExpiry) {
              setExpiryDate(data.subscriptionExpiry.toDate());
            }
          } else {
            setSubscriptionStatus('none');
          }
          
          if (data.hasUsedTrial) {
            setHasUsedTrial(true);
          }
        }
      });

      return unsubscribe;
    } catch (error: any) {
      console.error("Error fetching subscription:", error);
    }
  };

  const fetchPaymentHistory = async (sid: string) => {
      try {
          // Payments are stored in the root 'payments' collection, filtered by schoolId
          // Note: This query requires a composite index on (schoolId, createdAt)
          const paymentsRef = collection(db, 'payments');
          const q = query(paymentsRef, where('schoolId', '==', sid), orderBy('createdAt', 'desc'), limit(5));
          const querySnapshot = await getDocs(q);
          const payments = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setPaymentHistory(payments);
      } catch (error: any) {
          console.error("Error fetching payment history:", error);
      }
  }

  useEffect(() => {
    // Fetch available plans from Firestore
    const fetchPlans = async () => {
      try {
        const plansRef = collection(db, 'subscription_plans');
        const plansSnap = await getDocs(plansRef);
        const plansData = plansSnap.docs
          .map(d => ({ id: d.id, ...d.data() } as SubscriptionPlan))
          .filter(p => p.visibility === 'public')
          .sort((a, b) => a.order - b.order);
        
        setPlans(plansData);
        if (plansData.length > 0) {
          setSelectedPlan(plansData[0]);
        }

        // Fetch global settings
        const globalRef = doc(db, 'system_config', 'subscription_settings');
        const globalSnap = await getDoc(globalRef);
        if (globalSnap.exists()) {
          const data = globalSnap.data();
          if (data.defaultTrialDays) {
            setDefaultTrialDays(data.defaultTrialDays);
          }
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setFetchingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    let detatchSnapshot: (() => void) | undefined;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Get user's school ID from claims or profile
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims;
        setUserClaims(claims);
        
        const userSchoolId = claims.schoolID as string;
        
        if (userSchoolId) {
          setSchoolId(userSchoolId);
          const unsubDetails = await fetchSubscriptionDetails(userSchoolId);
          if (typeof unsubDetails === 'function') detatchSnapshot = unsubDetails;
          fetchPaymentHistory(userSchoolId);
        }
      }
    });

    return () => {
        unsubscribe();
        if (detatchSnapshot) detatchSnapshot();
    }
  }, []);



  const handleClaimTrial = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const claimTrial = httpsCallable(functions, 'claimFreeTrial');
      const result = await claimTrial({ schoolId });
      const data = result.data as any;
      
      if (data.success) {
        toast({
          title: "Trial Activated!",
          description: `You now have ${defaultTrialDays} days of free access.`,
        });
        // Refresh status
        fetchSubscriptionDetails(schoolId);
      }
    } catch (error: any) {
      console.error("Error claiming trial:", error);
      toast({
        title: "Trial Claim Failed",
        description: error.message || "Could not activate trial.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMpesaPayment = async () => {
    setLastPaymentError(null);
    
    if (!selectedPlan) {
      toast({
        title: "No Plan Selected",
        description: "Please select a subscription plan.",
        variant: "destructive"
      });
      return;
    }

    if (!phoneNumber || !phoneNumber.startsWith('254')) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid Safaricom number starting with 254.",
        variant: "destructive"
      });
      return;
    }

    if (!schoolId) {
      setLastPaymentError("School ID is NULL - user claims don't have schoolID");
      toast({
        title: "Error",
        description: "School ID not found. Please refresh the page.",
        variant: "destructive"
      });
      return;
    }

    const totalAmount = selectedPlan.prices.KES + (selectedPlan.setupFee || 0);

    const paymentData = {
      phoneNumber,
      amount: totalAmount,
      schoolId,
      planId: selectedPlan.id
    };

    // Store for debug panel
    setLastPaymentData(paymentData);
    addDebugLog('info', 'Calling initiateMpesaPayment function', paymentData);

    setLoading(true);
    try {
      const initiatePayment = httpsCallable(functions, 'initiateMpesaPayment');
      const response = await initiatePayment(paymentData);

      const data = response.data as any;
      addDebugLog('success', 'Payment function response', data);

      toast({
        title: "STK Push Sent",
        description: data.message || `Please check your phone (${phoneNumber}) to complete the payment.`,
      });

      // In a real app, you would listen for the payment confirmation via webhook/firestore update
    } catch (error: any) {
      addDebugLog('error', 'Payment initiation failed', { 
        message: error.message, 
        code: error.code,
        details: error.details 
      });
      console.error("Payment initiation failed:", error);
      toast({
        title: "Payment Failed",
        description: error.message || "Could not initiate M-Pesa payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Debug Button - Fixed Position */}
      <Button 
        variant="outline" 
        size="sm" 
        className="fixed bottom-4 right-4 z-50 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border-yellow-300"
        onClick={() => setShowDebug(true)}
      >
        <Bug className="h-4 w-4 mr-2" />
        Debug
      </Button>

      {/* Debug Dialog */}
      <Dialog open={showDebug} onOpenChange={setShowDebug}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Subscription Debug Panel
            </DialogTitle>
            <DialogDescription>
              Debug information for troubleshooting payment issues
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Current State */}
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <h3 className="font-semibold mb-2">Current State</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">School ID:</span> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{schoolId || 'null'}</code></div>
                <div><span className="text-gray-500">Status:</span> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{subscriptionStatus}</code></div>
                <div><span className="text-gray-500">Phone:</span> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{phoneNumber || 'not entered'}</code></div>
                <div><span className="text-gray-500">Plans Loaded:</span> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{plans.length}</code></div>
                <div className="col-span-2"><span className="text-gray-500">Selected Plan:</span> <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{selectedPlan ? `${selectedPlan.name} (${selectedPlan.id}) - KES ${selectedPlan.prices.KES}` : 'none'}</code></div>
              </div>
            </div>

            {/* User Claims */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h3 className="font-semibold mb-2">User Claims (from Firebase Auth)</h3>
              <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded overflow-x-auto max-h-40">
                {userClaims ? JSON.stringify(userClaims, null, 2) : 'No claims loaded yet'}
              </pre>
            </div>

            {/* Event Logs */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h3 className="font-semibold mb-2">Event Logs ({debugLogs.length})</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {debugLogs.length === 0 ? (
                  <p className="text-sm text-gray-500">No logs yet. Interact with the page to generate logs.</p>
                ) : (
                  debugLogs.map((log, i) => (
                    <div 
                      key={i} 
                      className={`text-xs p-2 rounded ${
                        log.type === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
                        log.type === 'success' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                        'bg-gray-100 dark:bg-gray-700'
                      }`}
                    >
                      <div className="flex justify-between">
                        <span className="font-mono">[{log.type.toUpperCase()}]</span>
                        <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      </div>
                      <div className="mt-1">{log.message}</div>
                      {log.data && (
                        <pre className="mt-1 text-xs bg-white/50 dark:bg-black/20 p-1 rounded overflow-x-auto">
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDebugLogs([])}>
              Clear Logs
            </Button>
            <Button onClick={copyDebugInfo} className="bg-blue-600 hover:bg-blue-700">
              <Copy className="h-4 w-4 mr-2" />
              Copy All Debug Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Current Subscription Status */}
        <Card className="md:col-span-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-none">
          <CardHeader>
            <CardTitle className="text-2xl">Current Subscription</CardTitle>
            <CardDescription className="text-blue-100">
              Manage your school's access and billing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div>
                <div className="text-sm opacity-80 uppercase tracking-wider font-semibold">Status</div>
                <div className="text-3xl font-bold flex items-center gap-2">
                  {subscriptionStatus === 'active' ? 'Active Plan' : 
                   subscriptionStatus === 'trial' ? `Free Trial (${defaultTrialDays} days)` : 
                   subscriptionStatus === 'none' ? 'No Subscription' : 'Expired'}
                  <Badge variant="secondary" className="text-blue-700 bg-white">
                    {subscriptionStatus.toUpperCase()}
                  </Badge>
                </div>
                {expiryDate && (
                  <div className="mt-2 text-blue-100">
                    Expires on: {expiryDate.toLocaleDateString(undefined, { dateStyle: 'long' })}
                  </div>
                )}
                {subscriptionStatus === 'active' && (
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="mt-4 font-semibold text-blue-700 hover:bg-blue-50"
                    onClick={() => navigate('/dashboard')}
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Go to Dashboard
                  </Button>
                )}
              </div>
              {subscriptionStatus !== 'active' && (
                <Button variant="secondary" className="whitespace-nowrap" onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}>
                  Upgrade Now
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Free Trial Offer */}
        {(subscriptionStatus === 'none' || subscriptionStatus === 'expired') && !hasUsedTrial && (
          <Card className="md:col-span-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white border-none">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Star className="h-6 w-6 text-yellow-300 fill-yellow-300" />
                Start Your Free Trial
              </CardTitle>
              <CardDescription className="text-green-50">
                Experience all premium features for {defaultTrialDays} days. No payment required.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button 
                size="lg" 
                variant="secondary" 
                className="w-full md:w-auto font-bold text-green-700"
                onClick={handleClaimTrial}
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Claim Free Trial
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Payment Form */}
        <Card id="plans">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-green-600" />
              Lipa na M-Pesa
            </CardTitle>
            <CardDescription>Select a plan and pay instantly via M-Pesa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Plan</Label>
              {fetchingPlans ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : plans.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No plans available. Please contact support.</p>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {plans.map((plan) => (
                    <div 
                      key={plan.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedPlan?.id === plan.id ? 'border-green-500 bg-green-50 dark:bg-green-900/20 ring-1 ring-green-500' : 'hover:border-gray-300'}`}
                      onClick={() => setSelectedPlan(plan)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{plan.name}</span>
                            {plan.badge && <Badge className="bg-yellow-100 text-yellow-800"><Star className="h-3 w-3 mr-1" />{plan.badge}</Badge>}
                          </div>
                          {plan.description && <p className="text-xs text-gray-500 mt-1">{plan.description}</p>}
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-green-700 dark:text-green-400">KES {plan.prices.KES.toLocaleString()}</span>
                          <div className="text-xs text-gray-500">/ {plan.billingInterval}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {plan.freeTrialDays > 0 && <Badge variant="outline" className="text-xs">{plan.freeTrialDays} day trial</Badge>}
                        {plan.setupFee > 0 && <Badge variant="outline" className="text-xs">KES {plan.setupFee} setup fee</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>M-Pesa Phone Number</Label>
              <Input 
                placeholder="2547XXXXXXXX" 
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                type="tel"
              />
              <p className="text-xs text-gray-500">Enter the number to receive the STK push prompt.</p>
            </div>
          </CardContent>
          <CardFooter className="flex-col gap-2">
            {selectedPlan && selectedPlan.setupFee > 0 && (
              <div className="w-full flex justify-between text-sm text-gray-600">
                <span>Setup Fee:</span>
                <span>KES {selectedPlan.setupFee.toLocaleString()}</span>
              </div>
            )}
            {selectedPlan && (
              <div className="w-full flex justify-between text-sm font-semibold">
                <span>Total:</span>
                <span>KES {(selectedPlan.prices.KES + (selectedPlan.setupFee || 0)).toLocaleString()}</span>
              </div>
            )}
            <Button 
              className="w-full bg-green-600 hover:bg-green-700 text-white mt-2" 
              onClick={handleMpesaPayment}
              disabled={loading || !selectedPlan}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : selectedPlan ? (
                <>
                  Pay KES {(selectedPlan.prices.KES + (selectedPlan.setupFee || 0)).toLocaleString()}
                </>
              ) : (
                'Select a Plan'
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* Plan Features */}
        <Card>
          <CardHeader>
            <CardTitle>Plan Features</CardTitle>
            <CardDescription>What you get with your subscription</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedPlan && selectedPlan.features.length > 0 ? (
              <ul className="space-y-3">
                {selectedPlan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">Select a plan to see its features.</p>
            )}
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card className="md:col-span-2">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Payment History
                </CardTitle>
            </CardHeader>
            <CardContent>
                {paymentHistory.length > 0 ? (
                    <div className="space-y-4">
                        {paymentHistory.map((payment) => (
                            <div key={payment.id} className="flex justify-between items-center border-b pb-2 last:border-0 last:pb-0">
                                <div>
                                    <div className="font-medium">{payment.description || 'Subscription Payment'}</div>
                                    <div className="text-xs text-gray-500">
                                        {payment.createdAt?.toDate().toLocaleDateString()} • {payment.transactionId}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant={payment.status === 'completed' ? 'default' : 'destructive'}>
                                        {payment.status}
                                    </Badge>
                                    <span className="font-bold">KES {payment.amount?.toLocaleString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-6 text-gray-500">No payment history found.</div>
                )}
            </CardContent>
        </Card>
      </div>
      
      <SuccessDialog 
        isOpen={showSuccessDialog} 
        onOpenChange={setShowSuccessDialog}
        expiryDate={expiryDate}
        onConfirm={() => {
          toast({ title: "Success", description: "Reloading your dashboard..." });
          console.log("SubscriptionManagement - OK clicked. Forcing HARD redirect.");
          // Using window.location.assign ensures a fresh request to the server
          window.location.assign('/dashboard');
        }}
      />
    </div>
  );
};

const SuccessDialog = ({ isOpen, onOpenChange, expiryDate, onConfirm }: { isOpen: boolean, onOpenChange: (open: boolean) => void, expiryDate: Date | null, onConfirm: () => void }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md text-center p-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
            <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white">Subscription Paid Successfully!</DialogTitle>
            <DialogDescription className="text-base pt-2">
              Your school's subscription has been activated. Thank you for your payment!
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg w-full border border-gray-100 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Next Payment Period / Expiry</p>
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-1">
              {expiryDate ? expiryDate.toLocaleDateString(undefined, { dateStyle: 'full' }) : 'N/A'}
            </p>
          </div>

          <DialogFooter className="w-full sm:justify-center mt-6">
            <Button 
              className="bg-green-600 hover:bg-green-700 text-white w-full py-6 text-lg font-bold"
              onClick={onConfirm}
            >
              OK - Go to Dashboard
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionManagement;
